import { useRouter } from "next/router";
import Link from "next/link";
import { useEffect, useState } from "react";
import useUser from "@/lib/useUser";
import {
  Box,
  Flex,
  Heading,
  Text,
  Switch,
  Select,
  Badge,
  Tooltip,
  Button,
} from "@radix-ui/themes";
import CampaignSearch from "@/components/NewCampaignViews/CampaignSearch";
import CampaignFavoriteButton from "@/components/NewCampaignViews/CampaignFavoriteButton";
import { updateStatus, useCampaign } from "@/services/campaign";
import useSWR from "swr";
import fetchJson from "@/lib/fetchJson";
import DashboardView from "@/components/NewCampaignViews/DashboardView/DashboardView";
import { IconUserCircle, IconLink } from "@tabler/icons-react";
import AdvancedView from "./AdvancedView/AdvancedView";
import Image from "next/image";
import { isValidId } from "@/lib/util";
import {
  isMetaReviewEmail,
  isMirroredCampaign,
  MAIN_AGENCY_ID,
} from "@/lib/constants";
import Unauthorized from "../Unauthorized";
import { Permission } from "@/services/permissions";

interface HeaderProps {
  id: string;
  setId: (id: string) => void;
  home: boolean;
}

const CampaignHeader = ({ id, setId, home }: HeaderProps) => {
  const router = useRouter();
  useEffect(() => {
    // Only update ID when router is ready and query.id is valid
    if (router.isReady && router.query.id) {
      setId(router.query.id as string);
    }
  }, [router.isReady, router.query.id, setId]);
  const {
    user,
    canManageUsers,
    canImpersonate,
    canCreateCampaigns,
    canEditCampaigns,
    canViewCampaigns,
    canDownloadReport,
    hasPermission,
  } = useUser();
  const { data, mutate } = useCampaign(id);
  const { data: ugc } = useSWR(
    isValidId(id) ? `/campaign/${id}/ugc` : null,
    fetchJson
  );
  const { data: streams } = useSWR(
    isValidId(id) ? `/campaign/${id}/streams` : null,
    fetchJson
  );

  // Meta App Review account: force the advanced view (no basic/dashboard).
  const isReviewer = isMetaReviewEmail(user?.email);
  const findView = (home) => {
    if (home) {
      return "dashboard";
    } else {
      return user?.campaignView || "dashboard";
    }
  };
  const [selectedView, setSelectedView] = useState(findView(home));
  useEffect(() => {
    if (isReviewer) setSelectedView("advanced");
  }, [isReviewer]);
  if (!user) return null;
  console.log("dataaaaaaacddd", data, user);
  const d = canCreateCampaigns || canEditCampaigns || canManageUsers;
  console.log("ddddddd", d);
  const editable = canImpersonate || d;
  const shareable =
    canImpersonate ||
    (canManageUsers && data?.agencyId == user.metadata.agencyId) ||
    ((canCreateCampaigns || canEditCampaigns) &&
      data?.agencyId == user.metadata.agencyId) ||
    canViewCampaigns;
  const transferable = canImpersonate;
  const isAdmin = canManageUsers || canImpersonate;
  let reportable =
    d ||
    (user?.metadata?.campaigns?.includes(parseInt(id)) && canViewCampaigns) ||
    canDownloadReport;
  if (user?.email === "nick@pulsemusicgroup.com") reportable = false;

  if (!id) return null;

  const updateCampaignStatus = async (status: string) => {
    await updateStatus(data.id, status);
    mutate();
  };

  // Check if this campaign is mirrored from another agency
  const isMirrored =
    user?.agencyId &&
    data?.agencyId &&
    isMirroredCampaign(user.agencyId, data.agencyId);

  return (
    <Box id="dashboard-bg" width="100%" minHeight="100%">
      <Flex direction="column" gap="4" pb="5">
        <Flex
          id="campaing-info"
          height="130px"
          gap="3"
          direction="row"
          justify="between"
          align="center"
        >
          <Flex gap="3" direction="row">
            <Box id="campaign-image">
              {(data?.song?.image || data?.artist?.image) && (
                <Image
                  src={`${process.env.NEXT_PUBLIC_DOMAIN}/image-upload/${
                    data.song?.image || data.artist?.image
                  }`}
                  width={120}
                  height={120}
                  alt=""
                  unoptimized
                  className="w-[120px] h-[120px] rounded-2xl object-cover"
                />
              )}
            </Box>
            <Flex direction="column" justify="center" py="1" gap="2">
              <Box id="campaign-name">
                {data && (
                  <Flex direction="row" align="center" gap="3">
                    <Heading size="8">{data?.name}</Heading>
                    <CampaignFavoriteButton campaignId={data.id} />
                    {isMirrored && (
                      <Tooltip content="This campaign is mirrored from CXC">
                        <Badge
                          color="blue"
                          variant="soft"
                          size="2"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            fontSize: "12px",
                            padding: "4px 8px",
                          }}
                        >
                          <IconLink size={12} />
                          CXC
                        </Badge>
                      </Tooltip>
                    )}
                  </Flex>
                )}
              </Box>
              <Flex direction="row" gap="4" align="center" wrap="wrap">
                {data && (
                  <Flex id="artist-name" direction="row" gap="1" align="center">
                    <IconUserCircle className="icon-dark" />
                    <Text size="2">{data?.artist?.name}</Text>
                  </Flex>
                )}
                {user?.agencyId === MAIN_AGENCY_ID &&
                  hasPermission(Permission.CAMPAIGN_CREATE) &&
                  data?.id && (
                    <Button asChild variant="outline" size="1">
                      <Link
                        href={{
                          pathname: "/social-requests",
                          query: {
                            preset: "trending",
                            campaignId: String(data.id),
                            ...(data.hashtags?.[0]?.hashtag
                              ? { suggestedHashtag: data.hashtags[0].hashtag }
                              : {}),
                          },
                        }}
                      >
                        Boost trending
                      </Link>
                    </Button>
                  )}
                <Box id="campaign-status">
                  {data && selectedView == "advanced" ? (
                    <Select.Root
                      disabled={!editable}
                      size="1"
                      defaultValue={
                        data?.status === "active"
                          ? "active"
                          : data?.status === "draft"
                          ? "draft"
                          : "archived"
                      }
                      onValueChange={(newStatus) => {
                        updateCampaignStatus(newStatus);
                      }}
                    >
                      <Select.Trigger />
                      <Select.Content>
                        <Select.Item value="active">
                          <Flex direction="row" align="center">
                            <div className="status-dot-active"></div>
                            <Text size="2">Active</Text>
                          </Flex>
                        </Select.Item>
                        <Select.Item value="archived">
                          <Flex direction="row" align="center">
                            <div className="status-dot-inactive"></div>
                            <Text size="2">Archived</Text>
                          </Flex>
                        </Select.Item>
                        <Select.Item value="draft">
                          <Flex direction="row" align="center">
                            <div className="status-dot-draft"></div>
                            <Text size="2">Draft</Text>
                          </Flex>
                        </Select.Item>
                      </Select.Content>
                    </Select.Root>
                  ) : (
                    <>
                      {data?.status == "active" ? (
                        <Flex direction="row" align="center">
                          <div className="status-dot-active"></div>
                          <Text size="2">Active</Text>
                        </Flex>
                      ) : data?.status == "draft" ? (
                        <Flex direction="row" align="center">
                          <div className="status-dot-draft"></div>
                          <Text size="2">Draft</Text>
                        </Flex>
                      ) : (
                        <Flex direction="row" align="center">
                          <div className="status-dot-inactive"></div>
                          <Text size="2">Archived</Text>
                        </Flex>
                      )}
                    </>
                  )}
                </Box>
              </Flex>
            </Flex>
          </Flex>
          <Flex
            id="campaign-search"
            direction="row"
            gap="2"
            justify="center"
            align="center"
          >
            <CampaignSearch />
            {/* TODO: this shouldn't be here , fix it after appropriate permission management implementation*/}
            {/* Reviewer is locked to advanced view, so the toggle is hidden. */}
            {!isReviewer &&
              (isAdmin ||
                [
                  "john@creatorxchange.co",
                  "Johnmahoney@creatorxchange.co",
                  "kal@creatorxchange.co",
                  "bryce@wve.la",
                  "pvinlaka@creatorxchange.co",
                ].includes(user?.email || "")) && (
              <Flex direction="row" gap="2" align="center">
                <Switch
                  checked={selectedView == "advanced"}
                  onCheckedChange={() => {
                    setSelectedView(
                      selectedView === "dashboard" ? "advanced" : "dashboard"
                    );
                  }}
                />
                <Flex direction="column">
                  <Text size="2">Advanced</Text>
                  <Text size="2">View</Text>
                </Flex>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>
      <div>
        <div className="hidden lg:block">
          {selectedView === "dashboard" ? (
            <DashboardView id={id} setId={setId} />
          ) : (
            <AdvancedView
              editable={editable}
              shareable={shareable}
              reportable={reportable}
              data={data}
              mutate={mutate}
              transferable={transferable}
            />
          )}
        </div>
      </div>
    </Box>
  );
};

export default CampaignHeader;
